import { ButtonSize, ButtonVariant } from "@galaxy-io/dls/buttons/Button";
import EmptyState from "@galaxy-io/dls/feedback/EmptyState";
import Skeleton, { SkeletonSize } from "@galaxy-io/dls/feedback/Skeleton";
import { useLocalStorage } from "@galaxy-io/dls/hooks/useLocalStorage";
import CheckboxInput, { CheckboxInputSize } from "@galaxy-io/dls/inputs/CheckboxInput";
import SelectInput, {
  SelectInputSize,
  SelectInputVariant,
} from "@galaxy-io/dls/inputs/SelectInput";
import Box, { BoxVariant } from "@galaxy-io/dls/layout/Box";
import { MenuItem } from "@galaxy-io/dls/menu/Menu";
import InfiniteTable from "@galaxy-io/dls/table/InfiniteTable";
import type { TableColumn, TableColumnLayout } from "@galaxy-io/dls/table/types";
import Text, { TextSize, TextVariant, TextWeight } from "@galaxy-io/dls/text/Text";
import { FontFamily, Side } from "@galaxy-io/dls/theme/enums";
import { ToastVariant } from "@galaxy-io/dls/toast/Toast";
import { useToast } from "@galaxy-io/dls/toast/useToast";
import type { AuditKind } from "@gtm/contracts/core/audit";
import { reviewActions } from "@gtm/contracts/core/review";
import { MAX_BULK_RECORDS } from "@gtm/contracts/http";
import { InfoIcon, ProhibitIcon, TextAlignLeftIcon } from "@phosphor-icons/react";
import { useMutation } from "@tanstack/react-query";
import {
  isValidElement,
  type ReactNode,
  useCallback,
  useMemo,
  useState,
  useSyncExternalStore,
} from "react";
import { useUrlState } from "@/app/navigation";
import { QueryState } from "@/components/query-state";
import { Button } from "@/components/ui/button";
import {
  DrawerList,
  DrawerRow,
  DrawerSection,
  DrawerStack,
} from "@/components/workspace/drawer-content";
import { ActorLabel, RecordAudit, useRecordAudit } from "@/components/workspace/record-audit";
import { reviewStatusOptions } from "@/components/workspace/review-status";
import { SurfaceDialog } from "@/components/workspace/surface-dialog";
import { TableFooter, type TablePaging } from "@/components/workspace/table-footer";
import {
  ReviewButtons,
  ReviewStatus,
  ReviewStatusEditor,
  useReviews,
} from "@/features/activity/review-actions";
import { SendMenu } from "@/features/activity/send-menu";
import { estimateColumnWidth } from "@/lib/table-layout";
import { toastDefaults } from "@/lib/toast";
import type { TableSort } from "./use-table-sort";

export type { TablePaging } from "@/components/workspace/table-footer";

import { Inset } from "@/components/workspace/people-styles";
import { TableLayout } from "@/components/workspace/table-layout";

function contentText(value: ReactNode): string {
  if (typeof value === "string" || typeof value === "number") return String(value);
  if (Array.isArray(value)) return value.map(contentText).join(" ");
  if (
    isValidElement<{ children?: ReactNode; label?: string; value?: ReactNode; name?: string }>(
      value,
    )
  )
    return contentText(
      value.props.children ?? value.props.label ?? value.props.value ?? value.props.name,
    );
  return "";
}
/**
 * Holds an identity for as long as a value's JSON shape is unchanged. Column
 * definitions are rebuilt whenever their inputs change identity, and rebuilding
 * them remounts every cell.
 */
function useStableShape<T>(value: T): T {
  const key = JSON.stringify(value);
  const [held, setHeld] = useState({ key, value });
  if (held.key !== key) setHeld({ key, value });
  return held.key === key ? held.value : value;
}
function subscribeViewport(callback: () => void) {
  const query = window.matchMedia("(max-width: 760px)");
  query.addEventListener("change", callback);
  return () => query.removeEventListener("change", callback);
}
// A paginated table is already sorted by the API; DLS uses this callback to disable local sorting.
const serverPageEnd = () => {};

type RecordRow = Record<string, ReactNode>;
export function RecordTable({
  label,
  auditKind,
  auditIds,
  columns,
  rows,
  details,
  reviewKeys,
  rowKeys,
  bulk,
  selectBatch,
  detailParam,
  paging,
  sort,
  fillHeight = true,
  rowDetails = false,
  editableStatus = false,
  deliveryActions = false,
  isLoading = false,
  error,
  onRetry,
  emptyState,
  onRowClick,
  rowActions,
}: {
  rowActions?: (row: RecordRow, index: number) => ReactNode;
  auditKind?: AuditKind;
  auditIds?: string[];
  rowKeys?: string[];
  /** Called with the row key when a row is clicked outside its own controls. */
  onRowClick?: (key: string) => void;
  detailParam?: string;
  rowDetails?: boolean;
  editableStatus?: boolean;
  deliveryActions?: boolean;
  isLoading?: boolean;
  error?: Error;
  onRetry?: () => void;
  emptyState?: ReactNode;
  paging?: TablePaging;
  sort?: TableSort;
  fillHeight?: boolean;
  label: string;
  columns: Array<{
    key: string;
    label: string;
    width?: number | "auto";
    minWidth?: number;
    pin?: "left" | "right";
    align?: "left" | "right";
  }>;
  rows: RecordRow[];
  reviewKeys?: string[];
  /** Resolves up to 500 matching records starting at the current page. */
  selectBatch?: () => Promise<Array<{ id: string; key: string }>>;
  /**
   * Row selection for tables outside the review lanes. Rows with no valid action
   * render a disabled checkbox, so a selection can never carry work that will
   * only be rejected by the server.
   */
  bulk?: {
    isSelectable: (key: string, index: number) => boolean;
    actions: (selected: string[], clear: () => void) => ReactNode;
  };
  details?: Array<{ key: string; label: string }>;
}) {
  const narrow = useSyncExternalStore(
    subscribeViewport,
    () => window.matchMedia("(max-width: 760px)").matches,
    () => false,
  );
  const [columnLayout, setColumnLayout] = useLocalStorage<TableColumnLayout>(
    `gtm:table-columns:${label}`,
    {},
  );
  const reviews = useReviews(reviewKeys || []);
  const [selection, setSelection] = useState<Record<string, boolean>>({});
  const { params, update } = useUrlState();
  const selectedKey = detailParam ? params.get(detailParam) : null;
  const rowIds = rowKeys || reviewKeys;
  const [batchKeys, setBatchKeys] = useState<Record<string, string>>({});
  const [sending, setSending] = useState(false);
  const { toast } = useToast();
  const batch = useMutation({
    mutationFn: async () => selectBatch!(),
    onSuccess: (records) => {
      setBatchKeys(Object.fromEntries(records.map((record) => [record.id, record.key])));
      setSelection(Object.fromEntries(records.map((record) => [record.id, true])));
      toast({
        variant: ToastVariant.SUCCESS,
        header: `${records.length} records selected`,
        description: "Matching your filters, starting at this page.",
        ...toastDefaults(ToastVariant.SUCCESS),
      });
    },
    onError: (error) =>
      toast({
        variant: ToastVariant.ERROR,
        header: "Could not expand selection",
        description: error.message,
        ...toastDefaults(ToastVariant.ERROR),
      }),
  });
  const keyById = {
    ...batchKeys,
    ...Object.fromEntries((reviewKeys || []).map((key, index) => [rowIds![index], key])),
  };
  const selectedKeys = Object.keys(selection)
    .filter((id) => selection[id] && keyById[id])
    .map((id) => keyById[id]);
  const selectionBusy = batch.isPending || sending;
  // Both tables poll, so a checked row can stop being actionable underneath us.
  const selectedRowIds = bulk
    ? (rowIds || []).filter((id, index) => selection[id] && bulk.isSelectable(id, index))
    : [];
  // Checkboxes belong to the rows on screen; paging away drops the selection.
  const [selectionPage, setSelectionPage] = useState(paging?.page);
  if (paging?.page !== selectionPage) {
    setSelectionPage(paging?.page);
    setSelection({});
  }
  const attributionIds = (auditIds || rowIds || []).map((id) =>
    id.replace(/^(person|observation):/, ""),
  );
  const attribution = useRecordAudit(auditKind, attributionIds);
  const externalDetails = detailParam === "activityId" || detailParam === "runId";
  const setSelected = useCallback(
    (key: string | null) => {
      if (detailParam)
        update({
          ...(key && ["activityId", "runId"].includes(detailParam)
            ? {
                activityId: null,
                runId: null,
                personId: null,
                person: null,
                personTab: null,
              }
            : {}),
          [detailParam]: key,
        });
    },
    [detailParam, update],
  );
  const selectedIndex =
    selectedKey === null ? -1 : rowIds ? rowIds.indexOf(selectedKey) : Number(selectedKey);
  const selected = rows[selectedIndex];
  const identityKey = ["activity", "title", "workflow", "repository"].find(
    (key) => selected?.[key],
  );
  // Both flags mean the row may be moved between review lanes inline.
  const laneSelector = deliveryActions || editableStatus;
  // A column definition that changes identity makes TanStack render the cell as
  // a new component, remounting it and taking focus out of any input a row
  // renders (the Schedules cron field). Everything the definitions below close
  // over therefore keeps its identity between renders, so typing in a cell no
  // longer rebuilds the table around the cursor.
  const hasDetails = !!details;
  const { states: reviewStates, busy: reviewsBusy, act: review } = reviews;
  const reviewsLoaded = reviews.loaded;
  const shape = useStableShape(columns);
  const keys = useStableShape(reviewKeys);
  const ids = useStableShape(rowIds);
  const auditIdShape = useStableShape(attributionIds);
  // Widths follow the rendered text, so they are measured on every render and
  // only rebuild the definitions when a measurement actually changes.
  const sizes = useStableShape(
    columns.map((c, index) => ({
      width:
        c.width === "auto"
          ? undefined
          : c.key === "person"
            ? estimateColumnWidth(
                c.label,
                rows.map((row) => contentText(row[c.key])),
                { minWidth: 170, maxWidth: 240, padding: 56 },
              )
            : index === 0
              ? undefined
              : (c.width ??
                estimateColumnWidth(
                  c.label,
                  rows.map((row) => contentText(row[c.key])),
                  { maxWidth: Number.MAX_SAFE_INTEGER, padding: 64 },
                )),
      // DLS needs the content floor to include a flexible column in overflow sizing.
      minWidth:
        c.width === "auto" || (index === 0 && c.key !== "person")
          ? Math.max(
              c.minWidth ?? 0,
              estimateColumnWidth(
                c.label,
                rows.map((row) => contentText(row[c.key])),
                {
                  maxWidth:
                    index === 0 && typeof c.width === "number" ? c.width : Number.MAX_SAFE_INTEGER,
                  padding: 64,
                },
              ),
            )
          : c.minWidth,
      // The first column carries no `size`, so it grows with the table. A
      // width given for it is that ceiling rather than a fixed measurement.
      maxWidth:
        index === 0 && c.key !== "person" && typeof c.width === "number" ? c.width : undefined,
    })),
  );
  const defs = useMemo<TableColumn<RecordRow>[]>(
    () => [
      ...(bulk
        ? [
            {
              id: "selection",
              canHide: false,
              header: "Select",
              width: 48,
              pin: Side.LEFT as const,
              canResize: false,
              cell: ({
                row,
                rowId,
                rowIndex,
              }: {
                row: RecordRow;
                rowId: string;
                rowIndex: number;
              }) => (
                <CheckboxInput
                  ariaLabel={`Select ${contentText(row[shape[0]?.key ?? ""]) || rowId}`}
                  isChecked={!!selection[rowId] && bulk.isSelectable(rowId, rowIndex)}
                  isDisabled={!bulk.isSelectable(rowId, rowIndex) || isLoading}
                  onChange={(checked) =>
                    setSelection((previous) => ({ ...previous, [rowId]: checked }))
                  }
                />
              ),
            },
          ]
        : []),
      ...shape.map(
        (c, index): TableColumn<RecordRow> => ({
          id: c.key,
          header: c.label,
          isRowHeader: index === 0,
          canHide: index > 0,
          pin:
            typeof sizes[index]?.width !== "number"
              ? undefined
              : c.pin === "right"
                ? narrow
                  ? undefined
                  : Side.RIGHT
                : c.pin === "left" || index === 0
                  ? Side.LEFT
                  : undefined,
          align: c.align === "right" ? ("right" as const) : undefined,
          ...sizes[index],
          accessor: (row: RecordRow) => contentText(row[c.key]),
          cell: ({ row }: { row: RecordRow }) => {
            const v = row[c.key];
            return typeof v === "string" || typeof v === "number" ? (
              <Text
                size={TextSize.BODY_MD}
                weight={index === 0 ? TextWeight.MEDIUM : TextWeight.REGULAR}
                family={c.align === "right" ? FontFamily.MONO : undefined}
                lineClamp={1}
              >
                {v}
              </Text>
            ) : (
              v || (
                <Text size={TextSize.BODY_SM} variant={TextVariant.TERTIARY}>
                  —
                </Text>
              )
            );
          },
        }),
      ),
      ...(auditKind
        ? [
            {
              id: "createdBy",
              header: "Created by",
              width: 200,
              cell: ({ rowIndex }: { rowIndex: number }) =>
                attribution.isPending ? (
                  <Box width={100}>
                    <Skeleton size={SkeletonSize.SMALL} />
                  </Box>
                ) : attribution.error ? (
                  <Text size={TextSize.BODY_SM}>Unavailable</Text>
                ) : (
                  <ActorLabel
                    actor={
                      attribution.data?.find((item) => item.id === auditIdShape[rowIndex])
                        ?.createdBy
                    }
                  />
                ),
            },
          ]
        : []),
      // Keep status visible; changes live in the native row menu.
      ...(keys
        ? [
            {
              id: "processing",
              canHide: false,
              accessor: () => null,
              header: "Status",
              width: 172,
              pin: narrow ? undefined : (Side.RIGHT as const),
              cell: ({ rowIndex }: { rowIndex: number }) => (
                <ReviewStatus review={reviewStates[keys![rowIndex]]} />
              ),
            },
          ]
        : []),
    ],
    [
      bulk,
      selection,
      shape,
      sizes,
      auditKind,
      attribution.isPending,
      attribution.error,
      attribution.data,
      auditIdShape,
      keys,
      reviewStates,
      isLoading,
      narrow,
    ],
  );
  // The array itself must keep its identity too: DLS rebuilds its own cell
  // wrappers whenever this prop changes.
  const sortable = useStableShape(sort?.columns ?? []);
  const tableColumns = useMemo(
    () =>
      defs.map((column) => ({
        ...column,
        canSort: sortable.includes(column.id!),
      })),
    [defs, sortable],
  );
  return (
    <>
      <TableLayout
        fillHeight={fillHeight}
        footer={
          <TableFooter
            label={label}
            total={error || isLoading ? undefined : paging ? paging.total : rows.length}
            selected={reviewKeys ? selectedKeys.length : bulk ? selectedRowIds.length : undefined}
            paging={
              paging
                ? {
                    ...paging,
                    busy:
                      paging.busy || reviews.busy || selectionBusy || isLoading || sort?.pending,
                  }
                : undefined
            }
            actions={
              // Nothing selected means none of these can do anything, and three
              // greyed-out controls under an empty table are noise rather than
              // an invitation. They appear with the first selection.
              reviewKeys && selectedKeys.length ? (
                <>
                  {selectBatch && selectedKeys.length > 0 && (
                    <Button
                      size={ButtonSize.SMALL}
                      label={batch.isPending ? "Selecting…" : `Select up to ${MAX_BULK_RECORDS}`}
                      variant={ButtonVariant.SECONDARY}
                      isDisabled={
                        selectionBusy ||
                        reviews.busy ||
                        isLoading ||
                        !!error ||
                        selectedKeys.length >= MAX_BULK_RECORDS
                      }
                      onClick={() => batch.mutate()}
                    />
                  )}
                  {(deliveryActions || editableStatus) && (
                    <SendMenu
                      bulk
                      keys={selectedKeys}
                      disabled={isLoading || selectionBusy || reviews.busy}
                      onPendingChange={setSending}
                      onSent={() => setSelection({})}
                    />
                  )}
                  {deliveryActions || editableStatus ? (
                    <Box width={200}>
                      <SelectInput
                        size={SelectInputSize.SMALL}
                        label="Set selected status"
                        fillWidth
                        value={null}
                        placeholder="Set selected status…"
                        ariaLabel="Set selected status"
                        variant={SelectInputVariant.BASE}
                        isDisabled={
                          !selectedKeys.length ||
                          selectionBusy ||
                          reviews.busy ||
                          !reviews.loaded ||
                          isLoading
                        }
                        options={reviewStatusOptions.map((option) => ({
                          label: option.label,
                          leading: option.icon,
                          id: reviewActions[option.value],
                          value: reviewActions[option.value],
                        }))}
                        onChange={async (option) => {
                          if (!option) return;
                          if (
                            await reviews.act(
                              selectedKeys,
                              option as
                                | "process"
                                | "ignore"
                                | "reset"
                                | "acknowledge"
                                | "draft"
                                | "save",
                            )
                          )
                            setSelection({});
                        }}
                      />
                    </Box>
                  ) : (
                    <>
                      <Button
                        size={ButtonSize.SMALL}
                        label="Process selected"
                        isDisabled={
                          !selectedKeys.length ||
                          selectionBusy ||
                          reviews.busy ||
                          !reviews.loaded ||
                          isLoading
                        }
                        onClick={async () => {
                          if (await reviews.act(selectedKeys, "process")) setSelection({});
                        }}
                      />
                      <Button
                        size={ButtonSize.SMALL}
                        label="Ignore selected"
                        icon={ProhibitIcon}
                        variant={ButtonVariant.SECONDARY}
                        isDisabled={
                          !selectedKeys.length ||
                          selectionBusy ||
                          reviews.busy ||
                          !reviews.loaded ||
                          isLoading
                        }
                        onClick={async () => {
                          if (await reviews.act(selectedKeys, "ignore")) setSelection({});
                        }}
                      />
                    </>
                  )}
                  <Button
                    size={ButtonSize.SMALL}
                    isDisabled={!selectedKeys.length || selectionBusy || reviews.busy}
                    label="Clear selection"
                    variant={ButtonVariant.TERTIARY}
                    onClick={() => setSelection({})}
                  />
                </>
              ) : bulk ? (
                <>
                  <CheckboxInput
                    size={CheckboxInputSize.SMALL}
                    label="Select all available"
                    isDisabled={
                      isLoading || !rowIds?.some((id, index) => bulk.isSelectable(id, index))
                    }
                    isChecked={
                      !!selectedRowIds.length &&
                      selectedRowIds.length ===
                        rowIds?.filter((id, index) => bulk.isSelectable(id, index)).length
                    }
                    isIndeterminate={
                      !!selectedRowIds.length &&
                      selectedRowIds.length <
                        (rowIds?.filter((id, index) => bulk.isSelectable(id, index)).length ?? 0)
                    }
                    onChange={(checked) =>
                      setSelection(
                        checked
                          ? Object.fromEntries(
                              (rowIds ?? [])
                                .filter((id, index) => bulk.isSelectable(id, index))
                                .map((id) => [id, true]),
                            )
                          : {},
                      )
                    }
                  />
                  {!!selectedRowIds.length && bulk.actions(selectedRowIds, () => setSelection({}))}
                </>
              ) : undefined
            }
          />
        }
      >
        {error ? (
          <QueryState
            error={error}
            loadingMessage={`Loading ${label.toLowerCase()}…`}
            onRetry={onRetry}
          />
        ) : (
          <Box variant={BoxVariant.BASE} height={fillHeight ? "100%" : undefined}>
            <InfiniteTable
              isLoading={isLoading}
              ariaLabel={label}
              onEndReached={sort ? serverPageEnd : undefined}
              sort={sort?.sorting}
              onSortChange={sort?.onSortingChange}
              columns={tableColumns}
              data={rows}
              getRowId={(_, index) => rowIds?.[index] || String(index)}
              isSelectable={!!reviewKeys && !selectionBusy && !reviews.busy && !isLoading}
              onRowClick={
                onRowClick || (rowDetails && details)
                  ? (row, event) => {
                      if (
                        (event.type === "click" && event.defaultPrevented) ||
                        (event.target instanceof Element &&
                          event.target.closest(
                            "a, button, input, [role=checkbox], [role=button], [role=combobox]",
                          ))
                      )
                        return;
                      const index = rows.indexOf(row);
                      const key = rowIds?.[index] || String(index);
                      if (onRowClick) onRowClick(key);
                      else setSelected(key);
                    }
                  : undefined
              }
              activeRowId={selectedKey ?? undefined}
              value={Object.keys(selection).filter((id) => selection[id])}
              onChange={(ids) => {
                if (selectionBusy || reviews.busy) return;
                if (selectBatch && ids.length > MAX_BULK_RECORDS) return;
                setSelection(
                  Object.fromEntries(
                    ids
                      .filter((id) => !bulk || bulk.isSelectable(id, rowIds?.indexOf(id) ?? -1))
                      .map((id) => [id, true]),
                  ),
                );
              }}
              canResizeColumns
              canCustomizeColumns
              rowActions={
                rowActions || keys || (hasDetails && !rowDetails)
                  ? (row, index) => {
                      const key = keys?.[index];
                      const state = key ? reviewStates[key]?.state : undefined;
                      const canReview =
                        key &&
                        (laneSelector ||
                          !state ||
                          ["acknowledged", "drafted", "saved", "unprocessed"].includes(state));
                      const custom = rowActions?.(row, index);
                      if (!custom && !canReview && !(hasDetails && !rowDetails)) return null;
                      return (
                        <>
                          {custom}
                          {canReview &&
                            (laneSelector ? (
                              <MenuItem
                                label="Set status"
                                isDisabled={reviewsBusy || !reviewsLoaded || isLoading}
                              >
                                {reviewStatusOptions.map((option) => (
                                  <MenuItem
                                    key={option.value}
                                    label={option.label}
                                    onSelect={() => void review([key], reviewActions[option.value])}
                                  />
                                ))}
                              </MenuItem>
                            ) : (
                              <>
                                <MenuItem
                                  label="Process"
                                  isDisabled={reviewsBusy || !reviewsLoaded || isLoading}
                                  onSelect={() => void review([key], "process")}
                                />
                                <MenuItem
                                  label="Ignore"
                                  isDisabled={reviewsBusy || !reviewsLoaded || isLoading}
                                  onSelect={() => void review([key], "ignore")}
                                />
                              </>
                            ))}
                          {hasDetails && !rowDetails && (
                            <MenuItem
                              label="Details"
                              onSelect={() =>
                                (onRowClick ?? setSelected)(ids?.[index] || String(index))
                              }
                            />
                          )}
                        </>
                      );
                    }
                  : undefined
              }
              columnLayout={columnLayout}
              onColumnLayoutChange={setColumnLayout}
              emptyState={
                emptyState ?? (
                  <EmptyState
                    role="status"
                    header="No records yet."
                    description="New records will appear here. If filters are active, try changing them."
                  />
                )
              }
            />
          </Box>
        )}
      </TableLayout>
      <SurfaceDialog
        title={`${label} details`}
        headerTitle={
          selected ? contentText(identityKey ? selected[identityKey] : undefined) || label : label
        }
        headerDescription={label}
        headerIcon={InfoIcon}
        flush
        open={!externalDetails && !!selectedKey}
        onClose={() => setSelected(null)}
        drawer
        headerActions={
          selected && reviewKeys ? (
            deliveryActions ? (
              <SendMenu keys={[reviewKeys[selectedIndex]]} />
            ) : editableStatus ? undefined : (
              <ReviewButtons
                compact
                review={reviews.states[reviewKeys[selectedIndex]]}
                busy={reviews.busy || !reviews.loaded || isLoading}
                onAction={(a) => reviews.act([reviewKeys[selectedIndex]], a)}
              />
            )
          ) : undefined
        }
      >
        {!selected && (
          <Inset>
            <Text>This record is no longer available.</Text>
          </Inset>
        )}
        {selected && (
          <DrawerStack>
            {auditKind && attributionIds[selectedIndex] && (
              <RecordAudit kind={auditKind} id={attributionIds[selectedIndex]} />
            )}
            <DrawerList>
              {reviewKeys && !deliveryActions && (
                <DrawerRow label="Status">
                  {editableStatus ? (
                    <ReviewStatusEditor
                      review={reviews.states[reviewKeys[selectedIndex]]}
                      busy={reviews.busy || !reviews.loaded}
                      onAction={(action) => reviews.act([reviewKeys[selectedIndex]], action)}
                    />
                  ) : (
                    <ReviewStatus review={reviews.states[reviewKeys[selectedIndex]]} />
                  )}
                </DrawerRow>
              )}
              {(details || columns)
                .filter(
                  (c) =>
                    c.key !== identityKey && !["description", "body", "result"].includes(c.key),
                )
                .map((c) => (
                  <DrawerRow key={c.key} label={c.label}>
                    {selected[c.key] ?? "—"}
                  </DrawerRow>
                ))}
            </DrawerList>
            {(details || columns)
              .filter((c) => ["description", "body", "result"].includes(c.key))
              .map((c) => (
                <DrawerSection key={c.key} header={c.label} icon={TextAlignLeftIcon} defaultIsOpen>
                  <Inset>
                    {typeof selected[c.key] === "object" ? (
                      selected[c.key]
                    ) : (
                      <Text>{selected[c.key] || "—"}</Text>
                    )}
                  </Inset>
                </DrawerSection>
              ))}
          </DrawerStack>
        )}
      </SurfaceDialog>
    </>
  );
}
